import type { ReactNode } from 'react';
import Tile, { type IconComponentName } from './Tile';
import s from './TileSelection.module.scss';

interface TileSelectionForm {
  setFieldValue: (
    field: string,
    value: string,
    shouldValidate?: boolean
  ) => Promise<void>;
}

interface TileSelectionField {
  name: string;
  value: string;
}

interface TileConfig {
  /** React-intl id of the label text */
  label: string;
  /** Value this tile represents */
  value: string;
  /** The name of the icon component to render, see icons/index.js for available icons */
  iconComponent: IconComponentName;
}

interface TileSelectionProps {
  field: TileSelectionField;
  form: TileSelectionForm;
  /** An array with one config object per tile */
  tileConfigs: TileConfig[];
  additionalContent?: ReactNode;
  /** Class to apply to the wrapping div */
  className?: string;
  /** Don't set a min height for use in a form */
  noMinHeight?: boolean;
  onNext?: (value: string) => void;
}

/**
 * A component that renders multiple tiles. This component does not
 * hold any state. The parent component needs to hold the currently selected value.
 */
function TileSelection({
  field,
  form,
  tileConfigs,
  additionalContent,
  className = '',
  noMinHeight = false,
  onNext,
}: TileSelectionProps) {
  const { name, value } = field;

  const handleChange = async (v: string): Promise<void> => {
    await form.setFieldValue(name, v, false);
    if (onNext) {
      onNext(v);
    }
  };

  let tileListClass = s.tileSelection;
  if (className) {
    tileListClass = `${tileListClass} ${className}`;
  }
  if (tileConfigs.length === 4) {
    tileListClass = `${tileListClass} ${s.tileCount4}`;
  }
  if (tileConfigs.length === 5) {
    tileListClass = `${tileListClass} ${s.tileCount5}`;
  }

  return (
    <div className={noMinHeight ? undefined : s.wrapper}>
      <ol className={tileListClass}>
        {tileConfigs.map((tileConfig, index) => (
          <li
            className={index !== tileConfigs.length - 1 ? s.tileWithMargin : ''}
            key={tileConfig.value}
          >
            <Tile
              name={name}
              label={tileConfig.label}
              value={tileConfig.value}
              iconComponent={tileConfig.iconComponent}
              checked={value === tileConfig.value}
              onChange={handleChange}
            />
          </li>
        ))}
      </ol>
      {additionalContent && (
        <div className={s.additionalContent}>{additionalContent}</div>
      )}
    </div>
  );
}

export default TileSelection;
export type {
  TileConfig,
  TileSelectionField,
  TileSelectionForm,
  TileSelectionProps,
};
